import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, AlertTriangle, RefreshCw, TrendingUp } from "lucide-react";
import { api } from "../../lib/api";
import { inputCls } from "./ui";

function FxCard() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const load = () => api.get("/admin/settings/fx").then((r) => setF(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!f) return <p className="text-[#5C5C5C]">Loading…</p>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/fx", { mode: f.mode, manual_rate: Number(f.manual_rate) || 0, buffer_pct: Number(f.buffer_pct) || 0 });
      setF(data); toast.success("Exchange rate saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); } finally { setSaving(false); }
  };
  const refresh = async () => {
    setRefreshing(true);
    try { await api.post("/admin/settings/fx/refresh"); await load(); toast.success("Live rate refreshed"); }
    catch { toast.error("Refresh failed"); } finally { setRefreshing(false); }
  };
  return (
    <div className="max-w-2xl" data-testid="admin-fx">
      <h2 className="font-display text-2xl text-[#0D530E] mb-1 flex items-center gap-2"><TrendingUp size={20} /> Exchange rate (£ → GH₵)</h2>
      <p className="text-[#5C5C5C] mb-5 text-sm">Controls the GH₵ price shown across the store. Use a manual rate, or auto-fetch the live market rate daily with a safety buffer.</p>
      <div className="bg-white border border-black/5 rounded-2xl p-6 space-y-4">
        <div className="rounded-xl bg-[#E7E1B1]/50 p-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-[#5C5C5C] font-semibold">Rate in use now</p>
            <p className="font-display text-3xl text-[#0D530E]" data-testid="fx-effective">£1 = GH₵ {Number(f.effective_rate).toFixed(2)}</p>
          </div>
          <button onClick={refresh} disabled={refreshing} data-testid="fx-refresh" className="flex items-center gap-1.5 text-sm font-semibold border border-[#306D29]/30 text-[#306D29] bg-white px-3 py-2 rounded-full hover:bg-[#306D29]/5 disabled:opacity-60">
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Refresh live
          </button>
        </div>
        <div className="flex gap-2">
          {["manual", "auto"].map((m) => (
            <button key={m} onClick={() => setF({ ...f, mode: m })} data-testid={`fx-mode-${m}`}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border ${f.mode === m ? "bg-[#306D29] text-white border-[#306D29]" : "bg-white text-[#5C5C5C] border-black/10"}`} style={{ transition: "background-color 0.2s ease" }}>
              {m === "manual" ? "Manual rate" : "Auto (live daily)"}
            </button>
          ))}
        </div>
        {f.mode === "manual" ? (
          <div>
            <label className="text-xs font-semibold text-[#5C5C5C]">Manual rate — GH₵ per £1</label>
            <input type="number" step="0.01" className={inputCls} value={f.manual_rate} onChange={set("manual_rate")} data-testid="fx-manual-rate" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl bg-[#FBF5DD] p-3 text-sm text-[#0D530E]">
              Live market rate: <b>GH₵ {Number(f.live_rate || 0).toFixed(4)}</b>{f.live_fetched_at ? ` · fetched ${new Date(f.live_fetched_at).toLocaleString()}` : " · not fetched yet"}
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C5C5C]">Safety buffer % (added on top of the live rate to cover FX swings & fees)</label>
              <input type="number" step="0.1" className={inputCls} value={f.buffer_pct} onChange={set("buffer_pct")} data-testid="fx-buffer" />
            </div>
            <p className="text-[11px] text-[#5C5C5C]">Charged rate = live × (1 + buffer). e.g. GH₵{Number(f.live_rate || 0).toFixed(2)} + {Number(f.buffer_pct) || 0}% = <b>GH₵{(Number(f.live_rate || 0) * (1 + (Number(f.buffer_pct) || 0) / 100)).toFixed(2)}</b>. Auto-refreshes daily.</p>
          </div>
        )}
        <button onClick={save} disabled={saving} data-testid="fx-save" className="bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold px-6 py-3 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>{saving ? "Saving…" : "Save exchange rate"}</button>
      </div>
    </div>
  );
}

const emptyTier = { max: 0, fee: 0 };
const emptyBand = { max_kg: 0, gh: 0 };
const PRow = ({ children }) => <div className="flex gap-2 items-center flex-wrap">{children}</div>;

function PricingCard() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings/pricing").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return <p className="text-[#5C5C5C]">Loading…</p>;
  const upd = (patch) => setP({ ...p, ...patch });
  const setTier = (i, k, v) => { const t = [...p.service_tiers]; t[i] = { ...t[i], [k]: Number(v) || 0 }; upd({ service_tiers: t }); };
  const setBand = (i, k, v) => { const b = [...p.sea_bands]; b[i] = { ...b[i], [k]: Number(v) || 0 }; upd({ sea_bands: b }); };
  const setDest = (i, v) => { const d = [...p.destinations]; d[i] = { ...d[i], surcharge: Number(v) || 0 }; upd({ destinations: d }); };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/pricing", {
        service_tiers: p.service_tiers, service_quote_over: Number(p.service_quote_over) || 0,
        sea_bands: p.sea_bands, air_bands: p.air_bands && p.air_bands.length ? p.air_bands : p.sea_bands,
        quote_over_kg: Number(p.quote_over_kg) || 0, destinations: p.destinations,
        default_weight_kg: Number(p.default_weight_kg) || 0.5,
      });
      toast.success("Pricing saved");
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };
  const numCls = inputCls + " w-28";
  return (
    <div className="max-w-3xl" data-testid="admin-pricing">
      <h2 className="font-display text-2xl text-[#0D530E] mb-1">Pricing — Service Fee &amp; Shipping</h2>
      <p className="text-[#5C5C5C] mb-5 text-sm">Customers see three numbers: Products, AFRIGATE Service Fee and Shipping. All values below are in GH₵.</p>
      <div className="bg-white border border-black/5 rounded-2xl p-6 space-y-7">
        <div>
          <p className="font-semibold text-[#0D530E] mb-2">Service fee — by product order value</p>
          <div className="space-y-2">
            {p.service_tiers.map((t, i) => (
              <PRow key={i}>
                <span className="text-sm text-[#5C5C5C] w-24">up to GH₵</span>
                <input type="number" className={numCls} value={t.max} onChange={(e) => setTier(i, "max", e.target.value)} data-testid={`tier-max-${i}`} />
                <span className="text-sm text-[#5C5C5C]">→ fee GH₵</span>
                <input type="number" className={numCls} value={t.fee} onChange={(e) => setTier(i, "fee", e.target.value)} data-testid={`tier-fee-${i}`} />
                <button onClick={() => upd({ service_tiers: p.service_tiers.filter((_, j) => j !== i) })} className="text-[#B23B3B]"><Trash2 size={15} /></button>
              </PRow>
            ))}
            <button onClick={() => upd({ service_tiers: [...p.service_tiers, { ...emptyTier }] })} className="text-sm text-[#306D29] font-semibold flex items-center gap-1"><Plus size={14} /> Add tier</button>
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-[#5C5C5C]">Above GH₵</span>
            <input type="number" className={numCls} value={p.service_quote_over} onChange={(e) => upd({ service_quote_over: e.target.value })} data-testid="service-quote-over" />
            <span className="text-sm text-[#5C5C5C]">→ custom quote</span>
          </div>
        </div>

        <div>
          <p className="font-semibold text-[#0D530E] mb-2">Sea shipping — by total order weight</p>
          <div className="space-y-2">
            {p.sea_bands.map((b, i) => (
              <PRow key={i}>
                <span className="text-sm text-[#5C5C5C] w-24">up to kg</span>
                <input type="number" step="0.01" className={numCls} value={b.max_kg} onChange={(e) => setBand(i, "max_kg", e.target.value)} data-testid={`band-kg-${i}`} />
                <span className="text-sm text-[#5C5C5C]">→ GH₵</span>
                <input type="number" className={numCls} value={b.gh} onChange={(e) => setBand(i, "gh", e.target.value)} data-testid={`band-gh-${i}`} />
                <button onClick={() => upd({ sea_bands: p.sea_bands.filter((_, j) => j !== i) })} className="text-[#B23B3B]"><Trash2 size={15} /></button>
              </PRow>
            ))}
            <button onClick={() => upd({ sea_bands: [...p.sea_bands, { ...emptyBand }] })} className="text-sm text-[#306D29] font-semibold flex items-center gap-1"><Plus size={14} /> Add band</button>
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-[#5C5C5C]">Over kg</span>
            <input type="number" className={numCls} value={p.quote_over_kg} onChange={(e) => upd({ quote_over_kg: e.target.value })} data-testid="quote-over-kg" />
            <span className="text-sm text-[#5C5C5C]">→ request a quote (oversized items too)</span>
          </div>
        </div>

        <div>
          <p className="font-semibold text-[#0D530E] mb-2">Destination surcharge (added to shipping)</p>
          <div className="space-y-2">
            {p.destinations.map((d, i) => (
              <PRow key={i}>
                <span className="text-sm text-[#0D530E] w-40">{d.name}</span>
                <span className="text-sm text-[#5C5C5C]">+ GH₵</span>
                <input type="number" className={numCls} value={d.surcharge} onChange={(e) => setDest(i, e.target.value)} data-testid={`dest-${i}`} />
              </PRow>
            ))}
          </div>
        </div>

        <button onClick={save} disabled={saving} data-testid="pricing-save" className="bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold px-6 py-3 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>{saving ? "Saving…" : "Save pricing"}</button>
      </div>
    </div>
  );
}

function ServiceLevelsCard() {
  const [levels, setLevels] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings/service-levels").then((r) => setLevels(r.data)).catch(() => {}); }, []);
  if (!levels) return null;
  const setL = (i, k, v) => setLevels(levels.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const add = () => setLevels([...levels, { key: `level_${Date.now()}`, name: "New level", description: "", eta: "", flat_gbp: 0, pct: 0, active: true }]);
  const rm = (i) => setLevels(levels.filter((_, idx) => idx !== i));
  const save = async () => {
    setSaving(true);
    const body = { levels: levels.map((l) => ({ key: l.key, name: l.name, description: l.description || "", eta: l.eta || "", flat_gbp: Number(l.flat_gbp) || 0, pct: Number(l.pct) || 0, active: !!l.active })) };
    try { await api.put("/admin/settings/service-levels", body); toast.success("Service levels saved"); }
    catch { toast.error("Failed to save service levels"); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-3xl" data-testid="admin-service-levels">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display text-2xl text-[#0D530E]">Service levels</h2>
        <button onClick={add} data-testid="add-service-level" className="flex items-center gap-1.5 text-sm bg-white border border-[#306D29]/30 text-[#306D29] px-3 py-1.5 rounded-full hover:bg-[#306D29]/5"><Plus size={14} /> Add level</button>
      </div>
      <p className="text-[#5C5C5C] mb-5 text-sm">Checkout speed/handling options. Fee = flat £ + % of product subtotal. Uncheck "Active" to hide a level from checkout.</p>
      <div className="space-y-4">
        {levels.map((l, i) => (
          <div key={i} className="bg-white border border-black/5 rounded-2xl p-5 space-y-3" data-testid={`sl-row-${i}`}>
            <div className="flex items-center justify-between">
              <input className={`${inputCls} max-w-[240px] font-semibold`} value={l.name} onChange={(e) => setL(i, "name", e.target.value)} data-testid={`sl-name-${i}`} placeholder="Name" />
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={!!l.active} onChange={(e) => setL(i, "active", e.target.checked)} data-testid={`sl-active-${i}`} /> Active</label>
                <button onClick={() => rm(i)} className="text-[#B23B3B] w-8 h-8 flex items-center justify-center" data-testid={`sl-del-${i}`}><Trash2 size={15} /></button>
              </div>
            </div>
            <textarea rows={2} className={inputCls} value={l.description} onChange={(e) => setL(i, "description", e.target.value)} placeholder="Description shown at checkout" data-testid={`sl-desc-${i}`} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><label className="text-xs font-semibold text-[#5C5C5C]">ETA / promise</label><input className={inputCls} value={l.eta} onChange={(e) => setL(i, "eta", e.target.value)} placeholder="e.g. 5–7 days" data-testid={`sl-eta-${i}`} /></div>
              <div><label className="text-xs font-semibold text-[#5C5C5C]">Flat fee £</label><input type="number" step="0.01" className={inputCls} value={l.flat_gbp} onChange={(e) => setL(i, "flat_gbp", e.target.value)} data-testid={`sl-flat-${i}`} /></div>
              <div><label className="text-xs font-semibold text-[#5C5C5C]">Percent of subtotal %</label><input type="number" step="0.1" className={inputCls} value={l.pct} onChange={(e) => setL(i, "pct", e.target.value)} data-testid={`sl-pct-${i}`} /></div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} data-testid="service-levels-save" className="mt-4 bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold px-6 py-3 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>{saving ? "Saving…" : "Save service levels"}</button>
    </div>
  );
}

export default function SettingsAdmin() {
  return (
    <div className="space-y-10">
      <FxCard />
      <PricingCard />
      <DangerZone />
    </div>
  );
}

function DangerZone() {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [includeMessages, setIncludeMessages] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = async () => {
    if (confirm !== "RESET") { toast.error('Type RESET to confirm'); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/fx/admin/reset", { confirm, include_messages: includeMessages });
      toast.success(`Reset complete — cleared ${data.total} records`);
      setOpen(false); setConfirm(""); setIncludeMessages(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-danger-zone">
      <h2 className="font-display text-2xl text-[#B23B3B] mb-1 flex items-center gap-2"><AlertTriangle size={20} /> Danger zone</h2>
      <p className="text-[#5C5C5C] mb-5 text-sm">Clear all <b>orders, payments and fulfilment data</b> for a fresh start. Your catalog (products, boxes, stores, categories, banners, promos), settings and staff logins are kept. This cannot be undone.</p>
      <div className="bg-white border border-[#B23B3B]/20 rounded-2xl p-6">
        <button onClick={() => setOpen(true)} data-testid="reset-open"
          className="flex items-center gap-2 bg-[#B23B3B] hover:bg-[#8f2f2f] text-white font-semibold px-5 py-3 rounded-full" style={{ transition: "background-color 0.2s ease" }}>
          <Trash2 size={16} /> Reset orders &amp; finances
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-[#FBF5DD] rounded-3xl p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="reset-modal">
            <h3 className="font-display text-2xl text-[#B23B3B] mb-2 flex items-center gap-2"><AlertTriangle size={20} /> Confirm reset</h3>
            <p className="text-sm text-[#5C5C5C] mb-4">This permanently deletes <b>all orders, payments, packages, containers, deliveries and tracking history</b>. Catalog and staff are safe. This cannot be undone.</p>
            <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
              <input type="checkbox" checked={includeMessages} onChange={(e) => setIncludeMessages(e.target.checked)} data-testid="reset-include-messages" />
              Also clear test reviews, requests &amp; contact messages
            </label>
            <label className="text-sm font-semibold">Type <span className="text-[#B23B3B]">RESET</span> to confirm</label>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input"
              className="w-full bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm mt-1 mb-4" placeholder="RESET" />
            <div className="flex gap-3">
              <button onClick={() => setOpen(false)} className="flex-1 py-2.5 rounded-full border border-black/10 text-sm">Cancel</button>
              <button onClick={reset} disabled={busy || confirm !== "RESET"} data-testid="reset-confirm-btn"
                className="flex-1 py-2.5 rounded-full bg-[#B23B3B] text-white text-sm font-semibold disabled:opacity-50">{busy ? "Resetting…" : "Reset everything"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
